/**
 * StampDutyFields — the state the new home is in, and the duty on buying it, as an estimate the
 * client can overwrite.
 *
 * Owner, 23 Sep 2026: "rather than just use a default rate are you able to have a stamp duty
 * calculator in the back-end … show the 'Estimated result' and allow them to override." So: pick the
 * state, the duty fills in from that state's own scale for the new home's price, and the figure stays
 * editable. Whenever the state or the price changes the estimate is struck again, which overwrites
 * an edited figure — a deliberate simplicity, said in the helper text.
 *
 * No state, no guess: with the state blank the duty is blank, the helper says why, and the model
 * charges nothing for duty. The same rule the health question follows.
 *
 * One component, so the Strategy Analysis Setup modal, the engine's settings, Manual Control and the
 * Downsizer module ask the question in the same words and fill it from the same calculator.
 */
import React, { useEffect } from 'react';
import { CurrencyInput } from './index.tsx';
import { calculateStampDuty, isStampDutyState, STAMP_DUTY_STATES } from '../../../../shared/src/calculation/stampDuty.ts';

export interface StampDutyFieldsProps {
    state: string | undefined | null;
    onStateChange: (state: string) => void;
    /** The new home's price the estimate is struck on. */
    newHomePrice: number | string | undefined | null;
    duty: number | string | undefined | null;
    onDutyChange: (duty: number) => void;
    /** Wraps each field, so the two screens' card styles can be matched. */
    fieldClassName?: string;
    /** Label style to match the surrounding form. */
    labelClassName?: string;
    /** Left blank on the modal, where the select is styled to match CurrencyInput. */
    selectClassName?: string;
    /** What is being bought — "the new home" by default; "the investment property" on the village allocator. */
    subject?: string;
}

const StampDutyFields = ({
    state,
    onStateChange,
    newHomePrice,
    duty,
    onDutyChange,
    fieldClassName = '',
    labelClassName = 'block text-sm font-medium text-gray-700 mb-1',
    selectClassName = 'w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500',
    subject = 'the new home',
}: StampDutyFieldsProps) => {
    const price = typeof newHomePrice === 'number' ? newHomePrice : parseFloat(String(newHomePrice ?? '')) || 0;
    const estimate = calculateStampDuty(state, price);

    // Re-struck whenever the state or the price changes. That overwrites a hand-typed figure, on
    // purpose: the alternative — remembering an override across a change of state — would keep a
    // Queensland quote on a Victorian home.
    useEffect(() => {
        if (estimate == null) return;
        const current = typeof duty === 'number' ? duty : parseFloat(String(duty ?? '')) || 0;
        if (current !== estimate) onDutyChange(estimate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, price]);

    const chosen = isStampDutyState(state) ? STAMP_DUTY_STATES.find((s) => s.code === state) : undefined;
    return (
        <>
            <div className={fieldClassName}>
                <label className={labelClassName}>State of {subject}</label>
                <select
                    value={chosen ? chosen.code : ''}
                    onChange={(e) => onStateChange(e.target.value)}
                    className={selectClassName}
                    aria-label={`State of ${subject}`}
                >
                    <option value="">Select a state…</option>
                    {STAMP_DUTY_STATES.map((s) => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Each state charges stamp duty on its own scale.</p>
            </div>
            <div className={fieldClassName}>
                <CurrencyInput
                    label="Stamp duty (estimate)"
                    name="stampDuty"
                    value={chosen ? (duty ?? '') : ''}
                    onChange={(v) => onDutyChange(v ?? 0)}
                    placeholder={chosen ? 'Estimated' : 'Choose a state first'}
                    disabled={!chosen}
                    className=""
                />
                <p className="text-xs text-gray-500 mt-1">
                    {chosen
                        ? `On ${chosen.name}'s standard scale for ${subject}, no concessions. Type over it if you have a quote.`
                        : `Choose a state above and we will estimate the duty on ${subject}. Until then, none is charged.`}
                </p>
            </div>
        </>
    );
};

export default StampDutyFields;
